import * as THREE from "three";
import {OrthographicCamera, Scene} from "three";
import Engine from "./Engine";
import Callout from "./Callout";
import ProjectSaver from "./ProjectSaver";
import SpriteBase from "./SpriteBase";
import {RegisterClassWithAPI} from "./api/Decorators";
import DefaultSceneManager from "./DefaultSceneManager";
import { CameraProjectionModes, CustomEventTypes, RenderLayers, SaveModificationTypes } from '../globals';
import HighlightSphere from "../classes/HighlightSphere";

let _geometry;
const tempPosition = new THREE.Vector3();
const tempRotation = new THREE.Quaternion();
const tempScale = new THREE.Vector3();

@RegisterClassWithAPI("ScopeCamera", {})
class ScopeCamera extends SpriteBase
{
	static spriteScale: THREE.Vector3 = new THREE.Vector3(0.075, 0.075, 0.075);
	static cameraBank: Map<string, ScopeCamera[]> = new Map();

	geometry: THREE.BufferGeometry;
	center: THREE.Vector2;
	name: string;
	updateCanvas: boolean;
	canvas: HTMLCanvasElement;
	perspectiveCamera: THREE.PerspectiveCamera;
	orthographicCamera: THREE.OrthographicCamera;
	currentCamera: THREE.Camera;
	helper: THREE.CameraHelper;
	onCameraAspectChanged?: (value: number) => void;

	static clearData()
	{
		ScopeCamera.cameraBank.clear();
	}

	static getSceneCameras(scene?: Scene): ScopeCamera[]
	{
		if(!scene?.userData.guid) return [];

		return ScopeCamera.cameraBank.get(scene.userData.guid) || [];
	}

	static getInteractiveModeCamera(scene: Scene)
	{
		const cameras = ScopeCamera.getSceneCameras(scene);
		if ( cameras )
		{
			for (const camera of cameras)
			{
				if (camera.userData.isInteractiveModeCamera) return camera;
			}
		}

		return null;
	}

	constructor()
	{
		super();

		if (_geometry === undefined)
		{

			_geometry = new THREE.BufferGeometry();

			const float32Array = new Float32Array([
				- 0.5, - 0.5, 0, 0, 0,
				0.5, - 0.5, 0, 1, 0,
				0.5, 0.5, 0, 1, 1,
				- 0.5, 0.5, 0, 0, 1
			]);

			const interleavedBuffer = new THREE.InterleavedBuffer(float32Array, 5);

			_geometry.setIndex([0, 1, 2, 0, 2, 3]);
			_geometry.setAttribute('position', new THREE.InterleavedBufferAttribute(interleavedBuffer, 3, 0, false));
			_geometry.setAttribute('uv', new THREE.InterleavedBufferAttribute(interleavedBuffer, 2, 3, false));
		}

		this.geometry = _geometry;
		if ( ScopeCamera.spriteMaterial)
		{
			this.material = ScopeCamera.spriteMaterial;
		}
		this.center = new THREE.Vector2(0.5, 0.5);
		this.name = "Camera";
		this.userData.camera =
		{
			fov: 60.0,
			orthographicSize: 5.0,
			nearplane: 0.01,
			farPlane: 15.0,
			projection: CameraProjectionModes.Perspective,
			aspect: 1.77,
		};
		this.userData.isInteractiveModeCamera = false;
		this.userData.interactiveCameraFocusPoint = "";

		this.updateCanvas = false;
		this.canvas = document.createElement("canvas");
		this.canvas.style.position = 'absolute';
		this.canvas.style.zIndex = "100";
		this.canvas.height = 540;
		this.canvas.width = this.canvas.height * this.userData.camera.aspect;

		this.perspectiveCamera = new THREE.PerspectiveCamera(this.userData.camera.fov, this.userData.camera.aspect, this.userData.camera.nearplane, this.userData.camera.farPlane);
		this.orthographicCamera = new THREE.OrthographicCamera(	this.userData.camera.orthographicSize * this.userData.camera.aspect * -0.5, this.userData.camera.orthographicSize * this.userData.camera.aspect * 0.5,
			this.userData.camera.orthographicSize * 0.5, this.userData.camera.orthographicSize * -0.5,
			this.userData.camera.nearplane, this.userData.camera.farPlane);
		this.currentCamera = this.perspectiveCamera;
		this.orthographicCamera.visible = false;
		this.helper = new THREE.CameraHelper(this.currentCamera);
		this.helper.visible = false;
		this.helper.setColors(
			new THREE.Color( 0xffaa00 ).convertLinearToSRGB(), 
			new THREE.Color( 0xff0000 ), 
			new THREE.Color( 0x00aaff).convertLinearToSRGB(), 
			new THREE.Color( 0x00ff00 ), 
			new THREE.Color( 0x333333).convertLinearToSRGB());


		this.orthographicCamera.userData.hideInHierarchy = true;
		this.perspectiveCamera.userData.hideInHierarchy = true;
		this.helper.userData.hideInHierarchy = true;

		this.attach(this.perspectiveCamera);
		this.attach(this.orthographicCamera);

		this.layers.set(RenderLayers.MainCameraOnly);
		this.helper.traverse(function(child) {child.layers.set(RenderLayers.MainCameraOnly);});
		this.orthographicCamera.updateProjectionMatrix();
	}

	copy(source, recursive = true)
	{
		THREE.Object3D.prototype.copy.call( this, source, false );
		if ( source.center !== undefined ) this.center.copy( source.center );

		this.setCameraAspect(source.userData.camera.aspect);
		this.setCameraProjectionMode(source.userData.camera.projection);

		if (recursive === true)
		{
			for (let i = 0; i < source.children.length; i++)
			{
				const child = source.children[i];
				if (!child.userData.hideInHierarchy || child instanceof Callout || child instanceof HighlightSphere)
				{
					this.add(child.clone());
				}
			}
		}

		return this;
	}

	toggleUpdateCanvas(value)
	{
		this.updateCanvas = value;
	}

	setCameraFov(value)
	{
		this.userData.camera.fov = value;
		this.perspectiveCamera.fov = this.userData.camera.fov;
		this.perspectiveCamera.updateProjectionMatrix();
		this.helper.update();
	}

	getCameraFov()
	{
		return this.userData.camera.fov;
	}

	setCameraAspect(value)
	{
		this.userData.camera.aspect = value;
		this.canvas.width = this.canvas.height * value;
		this.perspectiveCamera.aspect = this.userData.camera.aspect;
		this.perspectiveCamera.updateProjectionMatrix();
		this.setCameraOrthographicSize(this.userData.camera.orthographicSize);
		this.helper.update();

		if (this.onCameraAspectChanged)
		{
			this.onCameraAspectChanged(value);
		}
	}

	getCameraAspect()
	{
		return this.userData.camera.aspect;
	}

	setCameraOrthographicSize(value)
	{
		this.userData.camera.orthographicSize = value;
		this.orthographicCamera.left = this.userData.camera.orthographicSize * this.userData.camera.aspect * -0.5;
		this.orthographicCamera.right = this.userData.camera.orthographicSize * this.userData.camera.aspect * 0.5;
		this.orthographicCamera.top = this.userData.camera.orthographicSize * 0.5;
		this.orthographicCamera.bottom = this.userData.camera.orthographicSize * -0.5;
		this.orthographicCamera.updateProjectionMatrix();
		this.helper.update();
	}

	getCameraOrthographicSize()
	{
		return this.userData.camera.orthographicSize;
	}

	setCameraNearPlane(value)
	{
		if(!value) return;
		this.userData.camera.nearplane = value;
		if ( this.currentCamera instanceof OrthographicCamera)
		{
			this.currentCamera.near = this.userData.camera.nearplane;
			this.currentCamera.updateProjectionMatrix();
		}
		this.helper.update();
	}

	getCameraNearPlane()
	{
		return this.userData.camera.nearplane;
	}

	setCameraFarPlane(value)
	{
		if(!value) return;
		this.userData.camera.farPlane = value;
		(this.currentCamera as THREE.OrthographicCamera).far = this.userData.camera.farPlane;
		(this.currentCamera as THREE.OrthographicCamera).updateProjectionMatrix();
		this.helper.update();
	}

	getCameraFarPlane()
	{
		return this.userData.camera.farPlane;
	}

	setCameraProjectionMode(value)
	{
		this.userData.camera.projection = value;
		if (value === CameraProjectionModes.Perspective)
		{
			this.currentCamera = this.perspectiveCamera;
			this.perspectiveCamera.fov = this.userData.camera.fov;
			this.orthographicCamera.visible = false;
		}
		else
		{
			this.currentCamera = this.orthographicCamera;
			this.perspectiveCamera.visible = false;

			this.orthographicCamera.near = this.userData.camera.nearplane;
			this.orthographicCamera.far = this.userData.camera.farPlane;
			this.orthographicCamera.updateProjectionMatrix();
		}

		this.currentCamera.visible = true;
		this.helper.camera = this.currentCamera;
		
		this.helper.update();
	}

	getCameraProjectionMode()
	{
		return this.userData.camera.projection;
	}

	getCameraIsInteractiveMode()
	{
		return this.userData.isInteractiveModeCamera;
	}

	setCameraIsInteractiveMode(value)
	{
		if (value)
		{
			for (const [sceneGuid, cameras] of ScopeCamera.cameraBank.entries())
			{
				for (const camera of cameras)
				{
					if (camera.userData.guid === this.userData.guid)
					{
						const cameras = ScopeCamera.cameraBank.get(sceneGuid);
						if ( cameras )
						{
							for (const camera of cameras)
							{
								if (camera.userData.isInteractiveModeCamera)
								{
									camera.setCameraIsInteractiveMode(false);
									ProjectSaver.getInstance().markModifiedObject(camera, SaveModificationTypes.Camera);
								}
							}
						}
						break;
					}
				}
			}

			this.userData.preInteractiveBackupValues =
			{
				projection: this.getCameraProjectionMode(),
				nearplane: this.getCameraNearPlane(),
				farPlane: this.getCameraFarPlane(),
				fov: this.getCameraFov(),
			};

			this.setCameraProjectionMode(CameraProjectionModes.Perspective);
			this.setCameraNearPlane(0.01);
			this.setCameraFarPlane(15);
			this.setCameraFov(60.0);
			this.userData.isInteractiveModeCamera = true;
		}
		else
		{
			if (this.userData.preInteractiveBackupValues)
			{
				this.setCameraProjectionMode(this.userData.preInteractiveBackupValues.projection);
				this.setCameraNearPlane(this.userData.preInteractiveBackupValues.nearplane);
				this.setCameraFarPlane(this.userData.preInteractiveBackupValues.farPlane);
				this.setCameraFov(this.userData.preInteractiveBackupValues.fov);
				delete this.userData.preInteractiveBackupValues;
			}

			this.userData.isInteractiveModeCamera = false;
		}
	}
	
	setCameraFocusPoint(focusTargetGUID:string)
	{
		const previousFocusTargetGUID = this.userData.interactiveCameraFocusPoint;
		this.userData.interactiveCameraFocusPoint = focusTargetGUID;

		const defaultSceneManager = DefaultSceneManager.getInstance();
		if (defaultSceneManager.isDefaultObject(this.userData.guid))
		{
			for (const linkedObject of defaultSceneManager.getLinkedObjects(this))
			{
				const linkedCamera = linkedObject as ScopeCamera;
				
				let parent = linkedCamera.parent;
				let linkedSceneGuid: string | undefined = undefined;
				while(parent)
				{
					if(parent instanceof THREE.Scene)
					{
						linkedSceneGuid = parent.userData.guid;
						break;
					}
					parent = parent.parent;
				}

				if (!linkedSceneGuid) 
				{
					console.warn("Could not find scene GUID for linked camera: ", linkedCamera.userData.guid);
					continue;
				}
				const currentLinkedFocusTarget = defaultSceneManager.getDefaultObject(linkedCamera.userData.interactiveCameraFocusPoint);
				const linkedTargetObject = defaultSceneManager.getLinkedObject(focusTargetGUID, linkedSceneGuid);
				if (previousFocusTargetGUID === (currentLinkedFocusTarget?.userData.guid ?? ""))
				{
					linkedCamera.userData.interactiveCameraFocusPoint = linkedTargetObject?.userData.guid ?? "";
					ProjectSaver.getInstance().markModifiedObject(linkedCamera, SaveModificationTypes.Reference);
					
				}
			}
		}
		document.dispatchEvent(new CustomEvent(CustomEventTypes.updatePropertiesComponent));
	}

	getCameraFocusPoint()
	{
		return this.userData.interactiveCameraFocusPoint;
	}

	onSelected()
	{
		this.helper.visible = true;
		Engine.getInstance().setPreviewCameraFromSelection();
	}

	onDeselected()
	{
		this.helper.visible = false;
		Engine.getInstance().setPreviewCameraFromSelection();
	}

	onDispose()
	{
		this.onCameraRemovedFromScene();
	}

	onCameraAddedToScene(sceneGuid)
	{
		if (!ScopeCamera.cameraBank.has(sceneGuid))
		{
			ScopeCamera.cameraBank.set(sceneGuid, []);
		}

		const cameras = ScopeCamera.cameraBank.get(sceneGuid);
		if(this.userData.isInteractiveModeCamera)
		{
			//WC20-2208 - override any existing interactive camera if such exists
			if ( cameras ) 
			{
				for (const camera of cameras)
				{
					if (camera.userData.isInteractiveModeCamera)
					{
						camera.setCameraIsInteractiveMode(false);
						ProjectSaver.getInstance().markModifiedObject(camera, SaveModificationTypes.Camera);
					}
				}
			}
		}

		if ( cameras)
		{
			cameras.push(this);
		}
		Engine.getInstance().getScene(sceneGuid)?.attach(this.helper);
	}

	onCameraRemovedFromScene()
	{
		for (const cameraList of ScopeCamera.cameraBank.values())
		{
			const index = cameraList.indexOf(this);
			if (index >= 0)
			{
				cameraList.splice(index, 1);
				this.helper.parent?.remove(this.helper);
				break;
			}
		}
	}

	updateMatrixWorld(force: boolean = false)
	{
		if (this.matrixAutoUpdate) this.updateMatrix();

		if (this.matrixWorldNeedsUpdate || force)
		{
			if (this.parent === null)
			{
				this.matrixWorld.copy(this.matrix);

			}
			else
			{
				this.matrixWorld.multiplyMatrices(this.parent.matrixWorld, this.matrix);
			}

			this.matrixWorldNeedsUpdate = false;

			force = true;
		}

		const children = this.children;
		for (let i = 0, l = children.length; i < l; i++)
		{
			if (!children[i].userData.hideInHierarchy) children[i].updateMatrixWorld(force);
		}

		this.matrixWorld.decompose(tempPosition, tempRotation, tempScale);
		tempScale.set(1, 1, 1);
		this.matrixWorld.compose(tempPosition, tempRotation, tempScale);

		for (let i = 0, l = children.length; i < l; i++)
		{
			if (children[i].userData.hideInHierarchy) children[i].updateMatrixWorld(force);
		}

		this.matrixWorld.decompose(tempPosition, tempRotation, tempScale);
		this.matrixWorld.compose(tempPosition, tempRotation, ScopeCamera.spriteScale);
	}
}

export default ScopeCamera;
